import { useState } from 'react'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import logo from '../../assets/logos/AI Analytics logo.svg'
import {
  AppShell,
  Text,
  Burger,
  Group,
  Box,
  UnstyledButton,
  Avatar,
  Menu,
  useMantineTheme,
  ActionIcon,
  TextInput,
  Stack
} from '@mantine/core'
import {
  IconLayoutDashboard,
  IconBook,
  IconSettings,
  IconChevronDown,
  IconLogout,
  IconBell,
  IconSearch,
  IconUser,
  IconHelp
} from '@tabler/icons-react'
import { useAuth } from '../../contexts/AuthContext'
import styles from './DashboardLayout.module.css'

const navItems = [
  { icon: IconLayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: IconBook, label: 'Courses', path: '/courses' },
  { icon: IconSettings, label: 'Settings', path: '/settings' },
]

function NavbarLink({ icon: Icon, label, path, onClick }) {
  const location = useLocation()
  const navigate = useNavigate()
  const isActive = location.pathname === path

  return (
    <UnstyledButton
      onClick={() => {
        navigate(path)
        onClick?.()
      }}
      p="sm"
      w="100%"
      bg={isActive ? 'blue.0' : 'transparent'}
      c={isActive ? 'blue.6' : 'gray.6'}
      className={styles.navLink}
    >
      <Group>
        <Icon size={20} />
        <Text size="sm">{label}</Text>
      </Group>
    </UnstyledButton>
  )
}

function UserAvatar({ src, ...props }) {
  return <Avatar
    src={src}
    size={35}
    radius="xl"
    {...props}
  />
}

export function DashboardLayout({ children }) {
  const theme = useMantineTheme()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const [opened, setOpened] = useState(false)

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ 
        width: 250, 
        breakpoint: 'sm',
        collapsed: { mobile: !opened }
      }}
      padding="md"
      bg="gray.0"
    >
      <AppShell.Header bg="white" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger
              opened={opened}
              onClick={() => setOpened((o) => !o)}
              hiddenFrom="sm"
              size="sm"
            />
            <Text size="xl" fw={500} visibleFrom="sm">
              Hi, {user?.name || 'User'}
            </Text>
          </Group>

          <Group>
            <ActionIcon
              size="lg"
              variant="subtle"
              radius="md"
              onClick={() => navigate('/help')}
              title="Help Center"
            >
              <IconHelp size={20} />
            </ActionIcon>

            <ActionIcon size="lg" variant="subtle" radius="md">
              <IconBell size={20} />
            </ActionIcon>

            <Menu
              width={200}
              position="bottom-end"
              transitionProps={{ transition: 'pop-top-right' }}
            >
              <Menu.Target>
                <UnstyledButton>
                  <Group gap={7}>
                    <UserAvatar src={user?.avatar} />
                    <IconChevronDown size={12} stroke={1.5} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Account</Menu.Label>
                <Menu.Item>
                  <Stack gap={2}>
                    <Text size="sm" fw={500}>{user?.name}</Text>
                    <Text size="xs" c="dimmed" style={{ 
                      maxWidth: '180px', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis' 
                    }}>
                      {user?.email}
                    </Text>
                  </Stack>
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconSettings size={14} stroke={1.5} />}
                  onClick={() => navigate('/settings')}
                >
                  Settings
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconHelp size={14} stroke={1.5} />}
                  onClick={() => navigate('/help')}
                >
                  Help Center
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item 
                  leftSection={<IconLogout size={14} stroke={1.5} />}
                  onClick={async () => {
                    await signOut()
                  }}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md" bg="white">
        <AppShell.Section>
          <Group mb="md" gap="xs">
            <img 
              src={logo} 
              alt="AI Analytics" 
              height="40"
              width="auto"
            />
          </Group>
        </AppShell.Section>

        <AppShell.Section grow>
          <Stack gap="xs">
            {navItems.map((item) => (
              <NavbarLink
                key={item.label}
                {...item}
                onClick={() => setOpened(false)}
              />
            ))}
          </Stack>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        {children || <Outlet />}
      </AppShell.Main>
    </AppShell>
  )
}